import { useState, useEffect } from 'react';
import defaultContent from '../config/homePageContent.json';

interface HomePageContent {
  heroSection: {
    title: string;
    subtitle: string;
    getStartedButton: string;
    loginButton: string;
    dashboardButton: string;
    giftCardValue: string;
  };
  quickAccessSection: {
    title: string;
    description: string;
    activateCard: {
      title: string;
      description: string;
      business: {
        title: string;
        description: string;
        buttonText: string;
      };
      customer: {
        title: string;
        description: string;
        buttonText: string;
      };
      qrCode: {
        description: string;
      };
    };
    checkBalance: {
      title: string;
      description: string;
      buttonText: string;
    };
    cashoutCard: {
      title: string;
      description: string;
      buttonText: string;
    };
    registerCard: {
      title: string;
      description: string;
      buttonText: string;
    };
  };
  networkSection: {
    title: string;
    description: string;
    highlight: {
      title: string;
      description: string;
      numberText: string;
      numberLabel: string;
    };
    points: {
      title: string;
      description: string;
    }[];
    buttonText: string;
  };
  featuresSection: {
    title: string;
    description: string;
    roles: {
      title: string;
      description: string;
      buttonText: string;
    }[];
  };
  howItWorksSection: {
    title: string;
    steps: {
      number: number;
      title: string;
      description: string;
    }[];
  };
  ctaSection: {
    title: string;
    description: string;
    registerButton: string;
    loginButton: string;
    dashboardButton: string;
  };
  campaignsSection: {
    title: string;
    description: string;
  };
}

// This function would normally fetch from an API, but for simplicity,
// we're just using the local JSON file as the default
const fetchHomePageContent = async (): Promise<HomePageContent> => {
  // In a real-world scenario, you might fetch this from a CMS or API
  // For example:
  // const response = await fetch('/api/content/homepage');
  // return await response.json();
  
  // For now, we'll just return the default content
  return defaultContent as HomePageContent;
};

const useHomePageContent = () => {
  const [content, setContent] = useState<HomePageContent>(defaultContent as HomePageContent);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        const data = await fetchHomePageContent();
        setContent(data);
        setError(null);
      } catch (err) {
        setError(err as Error);
        console.error('Failed to load homepage content:', err);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, []);

  return { content, loading, error };
};

export default useHomePageContent;
export type { HomePageContent };